import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { FormattedMessage, FormattedHTMLMessage, FormattedDate, injectIntl, intlShape } from 'react-intl'
import { toInteger, has, filter, each, values, uniq, map, flatten, isPlainObject, uniqBy, isNil, cloneDeep } from 'lodash'

import { callApi } from '../services/fetchService'
import { decodePatient } from '../services/encodingService'
import { openPatientRecord, openConsultation, closeConsultation } from '../actions'

import biologyView from '../utils/biologyView.json'
import biologyViewTherapist from '../utils/biologyViewTherapist.json'

import FaIcon from '../components/FaIcon'
import { Spin, Row, Col, Table, Card, Tooltip, Switch, Button, Select, message } from 'antd'
const { Column } = Table

const groups = uniq(map(flatten(biologyView), 'group'))



/**
 * Global functions
 */
const getIndicators = (item, specificNorms) => {
    // Difference indicator
    let diff
    if (item.value < item.previous) {
        diff = <FaIcon icon="chevron-down" style={{ marginRight: 10 }} />
    } else if (item.value > item.previous) {
        diff = <FaIcon icon="chevron-up" style={{ marginRight: 10 }} />
    }
    // Calculate norm margin
    let margin = item.previous === undefined || item.previous === null || diff ? 10 : 32
    // Norm indicator
    let norm
    if (item.value < item.down) {
        norm = <FaIcon icon={["fas", "circle"]} color="blue" style={{ marginRight: margin }} />
    } else if (item.value > item.up) {
        norm = <FaIcon icon={["fas", "circle"]} color="red" style={{ marginRight: margin }} />
    } else {
        norm = <FaIcon icon="circle" style={{ marginRight: margin }} />
    }
    // Tooltip or not
    if (specificNorms) {
        return <Tooltip title={(item.down + ' - ' + item.up)}>{norm}{diff}</Tooltip>
    } else {
        return <span>{norm}{diff}</span>
    }
}

const getFormattedNumer = number => {
    if (number === undefined || number === null) {
        return ""
    }
    if (number === 0) {
        return 0
    }
    if (number < 0.01) {
        return Number(number).toExponential(2)
    }
    return Number(number).toFixed(2)
}

const isNormsSpecific = (item) => {
    return item.up === null || item.up === undefined
}

const hasMinOneKey = (object, keys) => {
    for (let key of keys) {
        if (has(object, key)) return true
    }
    return false
}

const computeData = (consultations, isTherapist = false, showOnlyOutNorms = false) => {
    // Structure to return
    const data = isTherapist ? cloneDeep(biologyViewTherapist) : cloneDeep(biologyView)
    // Get items into object for better search. Some items are in double, so for each item add table
    const indexedItems = []
    data.forEach(group => {
        group.forEach(elem => {
            if (indexedItems[elem.name]) indexedItems[elem.name].push(elem)
            else indexedItems[elem.name] = [elem]
        })
    })
    // Go through all biologies to add the elements to the items (data structure to return)
    each(consultations, consultation => {
        each(uniqBy(consultation.biology, 'name'), element => { // Uniq because il old biologies, some indexes are in double because in multiple axes
            const items = indexedItems[element.name]
            // If item must be shown (view)
            if (!items) return
            // If element has not a null value
            if (element.value === null) return
            // Add data
            each(items, item => {
                const toAdd = cloneDeep(element)
                toAdd.group = item.group
                if (!isNil(item._lastConsultId)) {
                    toAdd.previous = item[item._lastConsultId].value
                    item[consultation.id] = toAdd
                    if ((item.up && item.up !== toAdd.up) || (item.down && item.down !== toAdd.down)) {
                        delete item.up
                        delete item.down
                    }
                } else {
                    item[consultation.id] = toAdd
                    item.up = toAdd.up
                    item.down = toAdd.down
                }
                item._lastConsultId = consultation.id
            })
        })
    })
    // Filter into norms if necessary
    if (showOnlyOutNorms) {
        return data.map(group => {
            return group.filter(item => {
                // Removes into norms items
                for (const key in item) {
                    if (isPlainObject(item[key]) && item[key].value < item[key].up && item[key].value > item[key].down) {
                        delete item[key]
                    }
                }
                // Keep it if there remains consultations
                return filter(values(item), isPlainObject).length !== 0
            })
        })
    }
    // Return
    return data
}



/**
 * One Biology Function view
 */

class _OneBiologyFunction extends Component {

    static propTypes = {
        comboboxes: PropTypes.object.isRequired,
        patient: PropTypes.object.isRequired,
        consultation: PropTypes.object.isRequired,
        consultations: PropTypes.array.isRequired,
        showOnlyOutNorms: PropTypes.bool.isRequired,
        session: PropTypes.object.isRequired
    }

    render() {
        const { consultation, showOnlyOutNorms, session } = this.props
        const isTherapist = session.user.permissions.indexOf('doctor') === -1

        if (!consultation.biology || !consultation.biology.length) {
            return (
                <div className="message" style={{ marginTop: "45px" }}>
                    <FaIcon icon="exclamation-circle" size="8x" />
                    <br /><br />
                    <span style={{ fontSize: "35px" }}><FormattedMessage id="patient.biology.noBiology" /></span>
                    <br /><br />
                    <FormattedMessage id="patient.biology.noBiology.description" />
                </div>
            )
        }

        // Create data for table
        let data = computeData([consultation], isTherapist, showOnlyOutNorms)

        return (
            <div>
                <Row type="flex" gutter={24}>
                    {
                        data.map((group, index) => (
                            <Col xl={12} key={groups[index]}>
                                <Card title={<FormattedMessage id={"biology." + groups[index]} />}>
                                    <Table dataSource={group} rowKey="name" size="middle" pagination={false}>
                                        <Column title={<FormattedMessage id="patient.biology.index" />} key="element" render={(record) => (<FormattedMessage id={"biology." + record.group + "." + record.name} />)} />
                                        <Column title={<FormattedDate value={consultation.date} />} key={"" + consultation.id} render={
                                            (record) => record[consultation.id]
                                                ? <span>{getIndicators(record[consultation.id])}{getFormattedNumer(record[consultation.id].value)}</span>
                                                : null
                                        } />)
                                        <Column title={<FormattedMessage id="patient.biology.norm" />} key="norm" render={
                                            (record) => record[consultation.id] && getFormattedNumer(record[consultation.id].value) !== ""
                                                ? (record[consultation.id].down + ' - ' + record[consultation.id].up)
                                                : null
                                        } />
                                    </Table>
                                </Card>
                            </Col>
                        ))
                    }
                </Row>
            </div>
        )
    }

}

const _mapStateToProps = (state, ownProps) => ({
    comboboxes: state.comboboxes,
    patient: state.patient,
    session: state.session
})

const OneBiologyFunction = connect(_mapStateToProps)(_OneBiologyFunction)



/**
 * Summary inside the view
 */

class _BiologyFunctionSummary extends Component {

    static propTypes = {
        consultations: PropTypes.array.isRequired,
        showOnlyOutNorms: PropTypes.bool.isRequired,
        session: PropTypes.object.isRequired
    }

    render() {
        const { consultations, showOnlyOutNorms, session } = this.props
        const isTherapist = session.user.permissions.indexOf('doctor') === -1

        // Create data for table
        let data = computeData(consultations, isTherapist, showOnlyOutNorms)

        if (!data.length) {
            return (
                <div className="message" style={{ marginTop: "45px" }}>
                    <FaIcon icon="exclamation-circle" size="8x" />
                    <br /><br />
                    <span style={{ fontSize: "35px" }}><FormattedMessage id="patient.biology.noBiology" /></span>
                    <br /><br />
                    <FormattedMessage id="patient.biology.noBiology.description" />
                </div>
            )
        }

        const consultationIds = filter(consultations, (item) => !!item.biology.length).map(consultation => consultation.id)

        return (
            <div>
                {
                    data.map((group, index) => (
                        <Card title={<FormattedMessage id={"biology." + groups[index]} />} key={groups[index]}>
                            <Table dataSource={group} rowKey="name" size="middle" pagination={false}>
                                <Column title={<FormattedMessage id="patient.biology.index" />} key="element" render={(record) => (<FormattedMessage id={"biology." + record.group + "." + record.name} />)} />
                                {
                                    filter(consultations, (item) => !!item.biology.length).map((consultation) => <Column title={<FormattedDate value={consultation.date} />} key={"" + consultation.id} render={
                                        (record) => record[consultation.id]
                                            ? <span>{getIndicators(record[consultation.id], isNormsSpecific(record))}{getFormattedNumer(record[consultation.id].value)}</span>
                                            : null
                                    } />)
                                }
                                <Column title={<FormattedMessage id="patient.biology.norm" />} key="norm" render={
                                    (record) => isNormsSpecific(record)
                                        ? (hasMinOneKey(record, consultationIds) ? "Variables" : "")
                                        : (record.down + ' - ' + record.up)
                                } />
                            </Table>
                        </Card>
                    ))
                }
            </div>
        )
    }

}


const _mapStateToProps2 = (state, ownProps) => ({
    session: state.session
})

const BiologyFunctionSummary = connect(_mapStateToProps2)(_BiologyFunctionSummary)



/**
 * PatientBiologyFunction view
 */

class PatientBiologyFunction extends Component {

    static propTypes = {
        openPatientRecord: PropTypes.func.isRequired,
        openConsultation: PropTypes.func.isRequired,
        match: PropTypes.object.isRequired,
        consultation: PropTypes.object,
        intl: intlShape.isRequired
    }

    state = {
        patientId: null,
        patient: {},
        consultations: null,
        loading: false,
        showOnlyOutNorms: false,
        selectedConsultations: []
    }

    fetch = () => {
        const { intl, openPatientRecord, consultation } = this.props
        this.setState({ loading: true })
        if (consultation) this.setState({ selectedConsultations: [consultation] })
        callApi([{
            endpoint: 'patients/' + this.state.patientId
        }, {
            endpoint: 'patients/' + this.state.patientId + '/consultations'
        }]).then((data) => {
            decodePatient(data[0]).then((decoded) => {
                data[0] = decoded
                openPatientRecord(data[0])
                this.setState({
                    patient: data[0],
                    consultations: data[1],
                    loading: false,
                    selectedConsultations: !this.state.selectedConsultations.length && data[1].length ? [data[1][data[1].length - 1]] : this.state.selectedConsultations
                })
            })
        }).catch((data) => {
            message.error(intl.formatMessage({ id: 'misc.unknownError' }))
            this.setState({ loading: false })
        })
    }

    componentWillReceiveProps(nextProps) {
        let id = toInteger(nextProps.match.params.id)
        if (this.state.patientId !== id) {
            this.setState({ patientId: id }, () => {
                this.fetch()
            })
        }
    }

    componentWillMount() {
        let id = toInteger(this.props.match.params.id)
        this.setState({ patientId: id })
    }

    componentDidMount() {
        this.fetch()
    }

    onSwitchChange = (checked) => {
        this.setState({ showOnlyOutNorms: checked })
    }

    print = () => {
        const { intl } = this.props
        const { patientId, selectedConsultations } = this.state
        let endpoint = 'patients/' + patientId + '/consultations/print'
        callApi({
            endpoint,
            body: {
                patient: this.state.patient,
                consultations: selectedConsultations
            },
            method: 'POST'
        }).then((data) => {
            window.open(data.url)
        }).catch((data) => {
            message.error(intl.formatMessage({ id: 'misc.unknownError' }))
        })
    }

    send = () => {
        const { intl } = this.props
        const { patientId, selectedConsultations } = this.state
        let endpoint = 'patients/' + patientId + '/consultations/send'
        callApi({
            endpoint,
            body: {
                patient: this.state.patient,
                consultations: selectedConsultations
            },
            method: 'POST'
        }).then((data) => {
            message.success(intl.formatMessage({ id: 'patient.biology.sent' }))
        }).catch((data) => {
            message.error(intl.formatMessage({ id: 'misc.unknownError' }))
        })
    }

    handleBiologySelection = (value) => {
        if (value.length === 0) return
        this.setState({ selectedConsultations: this.state.consultations.filter(c => value.includes(c.id)) })
    }

    handleSelectAllBiology = () => {
        this.setState({ selectedConsultations: [...this.state.consultations] })
    }

    render() {
        const { patient, loading, consultations, showOnlyOutNorms, selectedConsultations } = this.state

        if (consultations === null || loading) {
            return (
                <div style={{ textAlign: 'center', marginTop: '50px' }}>
                    <Spin size="large" />
                </div>
            )
        }

        return (
            <div>
                <h1><FaIcon icon="user" fixedWidth /><FormattedHTMLMessage id="patient.biology.title" values={patient} /></h1>
                <div className="action-bar">
                    <FormattedMessage id="patient.biology.showOnlyOutNorms" /> <Switch checkedChildren={<FaIcon icon="check" />} unCheckedChildren={<FaIcon icon="times" />} style={{ marginLeft: 5 }} onChange={this.onSwitchChange} />
                    <Button onClick={this.print}><FaIcon icon="print" /> <FormattedMessage id="misc.print" /></Button>
                    {patient.email ? <Button onClick={this.send}><FaIcon icon="paper-plane" /> <FormattedMessage id="patient.biology.sendToPatient" /></Button> : null}
                </div>
                <div style={{ marginBottom: 15 }}>
                    <FormattedMessage id="patient.biology.selectBiologies" /> :&nbsp;
                    <Select mode="multiple" style={{ width: '500px' }} value={selectedConsultations.map(c => c.id)} onChange={this.handleBiologySelection}>
                        {consultations.map(c => <Select.Option key={c.id} value={c.id}><FormattedDate value={c.date} /></Select.Option>)}
                    </Select>
                    <Button style={{ marginLeft: 7 }} onClick={this.handleSelectAllBiology}><FormattedMessage id="patient.biology.selectAllBiologies" /></Button>
                </div>
                {
                    !consultations.length ? (
                        <div className="message" style={{ marginTop: "45px" }}>
                            <FaIcon icon="exclamation-circle" size="8x" />
                            <br /><br />
                            <span style={{ fontSize: "35px" }}><FormattedMessage id="patient.misc.noConsultations" /></span>
                            <br /><br />
                            <FormattedMessage id="patient.misc.noConsultations.description" />
                        </div>
                    ) : (
                        <div>
                            {selectedConsultations.length > 1
                                ? <BiologyFunctionSummary consultations={selectedConsultations} showOnlyOutNorms={showOnlyOutNorms} />
                                : <OneBiologyFunction consultation={selectedConsultations[0]} consultations={consultations} showOnlyOutNorms={showOnlyOutNorms} />
                            }
                        </div>
                    )
                }

            </div>
        )
    }

}

const mapStateToProps = (state, ownProps) => ({
    consultation: state.consultation
})

export default injectIntl(withRouter(connect(mapStateToProps, {
    openPatientRecord,
    openConsultation,
    closeConsultation
})(PatientBiologyFunction)))


// WEBPACK FOOTER //
// ./src/views/PatientBiologyFunction.js